import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { ExclamationCircleIcon } from '@heroicons/react/16/solid';

interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  name: string;
  placeholder?: string;
  optional?: boolean;
  value?: string;
  isInvalid?: boolean;
  validationMessage?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      name,
      placeholder,
      optional = false,
      value,
      isInvalid = false,
      validationMessage = '',
      onChange,
      onBlur,
      ...props
    },
    ref
  ) => {
    return (
      <div>
        <label
          htmlFor={name}
          className='block text-sm leading-5 font-medium text-gray-900 font-inter'
        >
          {label}{' '}
          {optional && (
            <span className='text-sm leading-5 font-normal font-inter text-gray-400 ml-2'>
              Optional
            </span>
          )}
        </label>
        <div className='mt-2 relative'>
          <textarea
            id={name}
            name={name}
            ref={ref}
            rows={4}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            aria-invalid={isInvalid}
            aria-describedby={isInvalid ? `${name}-error` : undefined}
            className={twMerge(
              'grow block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base sm:pr-9 sm:text-sm px-3 py-1.5 text-base placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 font-inter border',
              isInvalid
                ? 'text-red-900 border-red-500 focus:outline-red-600 focus:border-red-500'
                : 'text-gray-950 border-gray-200 focus:outline-blue-200 focus:border-blue-200'
            )}
            {...props}
          />
          {isInvalid && (
            <span className='pointer-events-none absolute top-2 right-0 flex items-center pr-3'>
              <ExclamationCircleIcon
                aria-hidden='true'
                className='h-5 w-5 text-red-500'
              />
            </span>
          )}
        </div>
        {isInvalid && validationMessage && (
          <p id={`${name}-error`} className='mt-2 text-sm text-red-600'>
            {validationMessage}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
